import { useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { useGetEndpointIntegrationsQuery } from '../../../features/marketplace/marketplaceApi'
import { CATEGORY } from '../../../features/marketplace/connectorCatalog'
import { paths } from '../../../app/routes'

const EMPTY = []

const partnerOf = (integration, shortName) =>
    (integration.source.shortName === shortName ? integration.target.shortName : integration.source.shortName)

const connects = (integration, a, b) => {
    const ends = [integration.source.shortName, integration.target.shortName]
    return ends.includes(a) && ends.includes(b)
}

const useIntegrationBuilder = (systems) => {
    const history = useHistory()
    const [firstId, setFirstId] = useState(null)
    const [secondId, setSecondId] = useState(null)
    const [integrationId, setIntegrationId] = useState(null)

    const first = systems.find((system) => system.id === firstId) || null
    const second = systems.find((system) => system.id === secondId) || null

    const { data: integrations = EMPTY, isFetching, isError } = useGetEndpointIntegrationsQuery(first ? first.id : skipToken)

    const partners = useMemo(() => {
        if (!first) return new Set()
        return new Set(integrations.map((integration) => partnerOf(integration, first.shortName)))
    }, [integrations, first])

    const matches = useMemo(
        () => (first && second ? integrations.filter((integration) => connects(integration, first.shortName, second.shortName)) : EMPTY),
        [integrations, first, second]
    )

    const chosen = matches.find((integration) => integration.id === integrationId) || matches[0] || null

    const isCompatible = (system) => partners.has(system.shortName)

    const select = (system) => {
        setIntegrationId(null)
        if (system.id === firstId) {
            setFirstId(secondId)
            setSecondId(null)
            return
        }
        if (system.id === secondId) {
            setSecondId(null)
            return
        }
        if (first && isCompatible(system)) {
            setSecondId(system.id)
            return
        }
        setFirstId(system.id)
        setSecondId(null)
    }

    const selectIntegration = (integration) => {
        const partner = systems.find((system) => system.shortName === partnerOf(integration, first.shortName))
        setSecondId(partner ? partner.id : null)
        setIntegrationId(integration.id)
    }

    const clear = () => {
        setFirstId(null)
        setSecondId(null)
        setIntegrationId(null)
    }

    const findByShortName = (shortName) => systems.find((system) => system.shortName === shortName) || null

    const placeInSlots = () => {
        const picked = [first, second].filter(Boolean)
        let source = picked.find((system) => system.category === CATEGORY.ERP) || null
        let target = picked.find((system) => system.category === CATEGORY.ECOMMERCE && system !== source) || null
        picked.filter((system) => system !== source && system !== target).forEach((system) => {
            if (!source) source = system
            else if (!target) target = system
        })
        return { source, target }
    }

    const { source, target } = chosen
        ? { source: findByShortName(chosen.source.shortName), target: findByShortName(chosen.target.shortName) }
        : placeInSlots()

    return {
        first,
        second,
        source,
        target,
        integrations,
        partnerCount: partners.size,
        systemFor: findByShortName,
        matches,
        chosen,
        isLoadingIntegrations: isFetching,
        integrationsError: isError,
        isCompatible,
        select,
        selectIntegration,
        chooseMatch: setIntegrationId,
        clear,
        proceed: () => chosen && history.push(paths.newConnector({ integration: chosen.id, from: first.id }))
    }
}

export default useIntegrationBuilder
